import { ClipboardList, Loader2 } from "lucide-react";
import { useId } from "react";
import { ErrorState } from "@/components/shared/error-state";
import { StatusPill } from "@/components/shared/status-badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { useAppSettings, useUpdateAppSettings } from "@/hooks/use-app-settings";

/**
 * Settings → Config → Feature availability.
 *
 * Sections of the app that are built but not yet switched on. A flag here is an
 * ordinary database row, not a build-time constant, so turning a feature on for
 * the whole workspace is a click rather than a release — see
 * `docs/performance-reviews-design.md` for why that distinction is load-bearing.
 */
export function SettingsFeatureAvailabilityPage() {
  const { data, isLoading, isError, error, refetch } = useAppSettings();

  if (isError) {
    return (
      <ErrorState
        message={
          error instanceof Error
            ? `Couldn't load feature availability: ${error.message}`
            : "Couldn't load feature availability"
        }
        onRetry={() => refetch()}
      />
    );
  }

  return (
    <div className="space-y-6">
      <p className="max-w-3xl text-sm text-muted-foreground">
        Sections of Enable that are built but not yet in use. Turning one on makes it visible to
        everyone it applies to, straight away — there is no release to wait for. Turning it off
        hides it again and leaves everything already recorded untouched.
      </p>

      <div className="divide-y divide-border rounded-lg border border-border">
        <FeatureRow
          icon={ClipboardList}
          title="Performance feedback and reviews"
          description="Review cycles, self and manager reviews, and the feedback history on a person's record. Still being built — expect it to be incomplete until the January review process starts."
          checked={data?.reviewsEnabled ?? false}
          loading={isLoading}
          field="reviewsEnabled"
        />
      </div>
    </div>
  );
}

/**
 * One feature, with its switch.
 *
 * The control carries a visible **On/Off word and a status pill**, and the word
 * is a `<label htmlFor>` so the text is a click target too. The first cut was a
 * bare unlabelled checkbox at the right edge of the row, which read as a status
 * marker rather than something operable — an admin reported being unable to
 * turn the feature on when they had every permission to do so. Don't reduce
 * this back to a naked checkbox.
 */
function FeatureRow({
  icon: Icon,
  title,
  description,
  checked,
  loading,
  field,
}: {
  icon: typeof ClipboardList;
  title: string;
  description: string;
  checked: boolean;
  loading: boolean;
  /** The single flag this row owns. */
  field: "reviewsEnabled";
}) {
  const update = useUpdateAppSettings();
  const id = useId();
  const busy = loading || update.isPending;

  return (
    <div className="flex items-start gap-4 p-4">
      <Icon className="mt-0.5 size-5 shrink-0 text-muted-foreground" aria-hidden />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="font-medium">{title}</p>
          <StatusPill
            tone={checked ? "success" : "neutral"}
            label={checked ? "In use" : "Not in use"}
          />
        </div>
        <p className="mt-1 text-sm text-muted-foreground">{description}</p>
      </div>
      <div className="flex shrink-0 items-center gap-2 pt-0.5">
        {busy && <Loader2 className="size-4 animate-spin text-muted-foreground" aria-hidden />}
        <Label
          htmlFor={id}
          className={busy ? "text-muted-foreground" : "cursor-pointer text-muted-foreground"}
        >
          {checked ? "On" : "Off"}
        </Label>
        <Checkbox
          id={id}
          checked={checked}
          disabled={busy}
          aria-label={`Turn ${title} ${checked ? "off" : "on"}`}
          // Sends only the flag it owns, so a future second row can't write this
          // one's stale value back over a change made elsewhere.
          onCheckedChange={(next) => update.mutate({ [field]: next === true })}
        />
      </div>
    </div>
  );
}
